import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { InspectionsProgramSimpleDto } from '../types/dtos/inspection';
import { createInspectionProgramService, CreateInspectionProgramDto } from '../services/inspection-program.service';
import { PaginationParams } from '../types/common';

const INSPECTION_PROGRAMS_KEY = 'inspection-programs';

export const useInspectionPrograms = (
  organizationId: string, 
  inspectionId: string, 
  params?: PaginationParams
) => {
  const inspectionProgramService = createInspectionProgramService(organizationId, inspectionId);

  return useQuery({
    queryKey: [INSPECTION_PROGRAMS_KEY, organizationId, inspectionId, params],
    queryFn: () => inspectionProgramService.findAll(params).then((res) => res.data),
    enabled: !!organizationId && !!inspectionId
  });
};

export const useInspectionProgram = (organizationId: string, programId: string, id: string) => {
  const inspectionProgramService = createInspectionProgramService(organizationId, programId);

  return useQuery({
    queryKey: [INSPECTION_PROGRAMS_KEY, organizationId, programId, id],
    queryFn: () => inspectionProgramService.findById(id).then((res) => res.data),
    enabled: !!organizationId && !!programId && !!id
  });
};

export const useInspectionProgramsByInspection = (organizationId: string, programId: string, inspectionId: string) => {
  const inspectionProgramService = createInspectionProgramService(organizationId, programId);

  return useQuery({
    queryKey: [INSPECTION_PROGRAMS_KEY, organizationId, programId, 'inspection', inspectionId],
    queryFn: () => inspectionProgramService.findByInspection(inspectionId).then((res) => res.data),
    enabled: !!organizationId && !!programId && !!inspectionId
  });
};

export const useCreateInspectionProgram = (organizationId: string, inspectionId: string) => {
  const queryClient = useQueryClient();
  const inspectionProgramService = createInspectionProgramService(organizationId, inspectionId);

  return useMutation({
    mutationFn: (data: CreateInspectionProgramDto) => 
      inspectionProgramService.create(data).then((res) => res.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [INSPECTION_PROGRAMS_KEY, organizationId, inspectionId] });
    }
  });
};

export const useUpdateInspectionProgram = (organizationId: string, programId: string) => {
  const queryClient = useQueryClient();
  const inspectionProgramService = createInspectionProgramService(organizationId, programId);

  return useMutation({
    mutationFn: ({ id, inspectionProgram }: { id: string; inspectionProgram: Partial<Omit<InspectionsProgramSimpleDto, 'id'>> }) =>
      inspectionProgramService.update(id, inspectionProgram as any).then((res) => res.data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: [INSPECTION_PROGRAMS_KEY, organizationId, programId] });
      queryClient.invalidateQueries({ queryKey: [INSPECTION_PROGRAMS_KEY, organizationId, programId, id] });
    }
  });
};

export const useUpdateInspectionProgramStatus = (organizationId: string, programId: string) => {
  const queryClient = useQueryClient();
  const inspectionProgramService = createInspectionProgramService(organizationId, programId);

  return useMutation({
    mutationFn: ({ id, status }: { id: string; status: string }) =>
      inspectionProgramService.updateStatus(id, status).then((res) => res.data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: [INSPECTION_PROGRAMS_KEY, organizationId, programId] });
      queryClient.invalidateQueries({ queryKey: [INSPECTION_PROGRAMS_KEY, organizationId, programId, id] });
    }
  });
};

export const useDeleteInspectionProgram = (organizationId: string, programId: string) => {
  const queryClient = useQueryClient();
  const inspectionProgramService = createInspectionProgramService(organizationId, programId);

  return useMutation({
    mutationFn: (id: string) => inspectionProgramService.delete(id).then((res) => res.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [INSPECTION_PROGRAMS_KEY, organizationId, programId] });
    }
  });
}; 